import { ArrowRight, BrainCircuit, Building2, Megaphone, MessageSquareText, ShieldCheck, Sparkles, Zap } from "lucide-react";
import { useCallback, useEffect, useState } from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import { FoundingRevenueBlock } from "@/components/landing/FoundingRevenueBlock";
import { FloatingOwnershipBot } from "@/components/landing/FloatingOwnershipBot";
import { InstitutionalConversionHero } from "@/components/landing/InstitutionalConversionHero";
import { InstitutionalRoiCalculator } from "@/components/landing/InstitutionalRoiCalculator";
import { LeadSovereigntyCard } from "@/components/landing/LeadSovereigntyCard";
import { MarketActivityBanner } from "@/components/landing/MarketActivityBanner";
import { RealTimeAssetIntelligenceStrip } from "@/components/landing/RealTimeAssetIntelligenceStrip";
import { SubscriptionPlansTable } from "@/components/SubscriptionPlansTable";
import { useSiteBranding } from "@/context/SiteContext";
import { useSeo } from "@/hooks/useSeo";
import { useStructuredData } from "@/hooks/useStructuredData";
import { api } from "@/lib/api";
import { assertRootDomainOnly } from "@/lib/routeIsolation";

const OFFER_COPY = "Singapore’s Smartest Property Platform — Powered by Top Agents";

const FEATURE_CARDS = [
  {
    key: "alpha-engine",
    title: "Stronger Buyer Conversations",
    body: "Use richer listing presentation, market context, and AI-generated support to sound more prepared in every serious buyer interaction.",
    icon: BrainCircuit,
  },
  {
    key: "signature-advisor",
    title: "24/7 Lead Capture",
    body: "The Signature Advisor keeps working when you are offline, helping you respond faster and stop late-night buyers from disappearing.",
    icon: MessageSquareText,
  },
  {
    key: "white-label",
    title: "Brand-Led Positioning",
    body: "Your portal looks and feels more credible than a generic profile page, helping clients see you as the advisor instead of just another listing source.",
    icon: Building2,
  },
];

const BIG_THREE_VALUE_PROPS = [
  {
    key: "lead-ownership",
    icon: ShieldCheck,
    title: "You Keep 100% of Your Leads",
    body: "No more competing with ten other agents for the same buyer. When a client contacts you through your portal, that lead belongs exclusively to you.",
  },
  {
    key: "ai-assistant",
    icon: BrainCircuit,
    title: "A 24/7 AI Assistant",
    body: "The Signature Advisor works while you sleep. It instantly answers property questions and engages late-night buyers so they don't bounce to another agent.",
  },
  {
    key: "social-cost",
    icon: Megaphone,
    title: "Zero Social Media Marketing Costs",
    body: "Generate professional social media content and post it instantly straight from your dashboard. You will never need to pay for a graphic designer or a social media manager again.",
  },
];

const LONG_TERM_VALUE_POINTS = [
  {
    key: "branded-website",
    title: "Your Own Branded Website",
    body: "Stop being just another generic profile on a massive directory. You get a premium, standalone digital headquarters that highlights your specific brand and listings.",
  },
  {
    key: "brand-value",
    title: "Build Long-Term Brand Value",
    body: "Instead of spending money to build a massive property aggregator's brand, every dollar and minute you spend drives traffic to your personal business.",
  },
];

const ROI_PROOF_POINTS = [
  "One extra serious seller or buyer-side conversion path can justify months of Pro Plan.",
  "The value is not just the website — it is faster response, stronger trust, and more owned lead flow.",
  "Instead of paying to strengthen someone else's platform, you are building your own long-term business asset.",
  "Every listing, report, and WhatsApp share works harder when it pushes traffic back to your own branded destination.",
];

const AGENT_OBJECTION_FAQ = [
  {
    key: "question-01",
    question: "Why pay for this if I already use property portals?",
    answer:
      "Regular property websites make you fight with dozens of other agents for the same buyer. With Pro Plan, you get your own personal space where every message goes straight to you, and no one else.",
  },
  {
    key: "question-02",
    question: "Is S$249/month really worth it for a solo agent?",
    answer:
      "Yes. Closing just one extra deal a year easily covers the cost. Most agents quickly find that saving time and getting better, more serious buyers makes it worth every cent.",
  },
  {
    key: "question-03",
    question: "Do I need technical skills to set this up?",
    answer:
      "Not at all. Our easy setup guide holds your hand through the whole process. Just check your CEA number, add a photo, and your page is ready to go in minutes.",
  },
  {
    key: "question-04",
    question: "What if I already have my own website?",
    answer:
      "That is perfectly fine. Pro Plan works smoothly right alongside it. You can keep your personal website to tell your story, and use Pro Plan to easily catch and organize new buyer messages.",
  },
  {
    key: "question-05",
    question: "How do I get people to visit my Pro Plan space?",
    answer:
      "Your Pro Plan space naturally attracts visitors from Google and our main website. We also give you easy buttons to share your property listings directly with your WhatsApp contacts and social media followers.",
  },
  {
    key: "question-06",
    question: "Am I locked into a long contract?",
    answer:
      "Not at all. You pay month by month. There are no yearly contracts or hidden cancellation fees. You have total control to pause or stop your plan anytime with just a few clicks.",
  },
  {
    key: "question-07",
    question: "How do you make sure the buyers are serious?",
    answer:
      "We save you time by asking buyers important questions before they reach you. Our system automatically checks their budget and when they want to move, so you only talk to real, ready buyers.",
  },
  {
    key: "question-08",
    question: "Is my client information safe and private?",
    answer:
      "Yes, completely. We follow all of Singapore's privacy rules to keep your details safe. Any buyer who contacts you belongs only to you, and we never share your contacts with anyone else.",
  },
  {
    key: "question-09",
    question: "Can I get rewarded for inviting other agents?",
    answer:
      "Yes! We want to reward you for helping the community grow. If you invite your peers to join, our system automatically tracks your referrals and pays you a commission for every agent who signs up through your link.",
  },
  {
    key: "question-10",
    question: "What happens if I get stuck or need help?",
    answer:
      "You are never on your own. We have a dedicated support team ready to help you with any questions. Whether you need help sharing a listing or updating your profile, we are just a quick message away.",
  },
];


const AGENT_ACTIVATION_STEPS = [
  {
    key: "choose-seat",
    title: "Choose your founding-seat entry point",
    body: "Start from the For Agents page, review the founding offer, and select the SG Prop Portal path that matches your advisory ambition.",
  },
  {
    key: "create-account",
    title: "Create your SG Prop Portal account",
    body: "Complete the signup flow so your portal, billing path, and activation record are attached to a single advisory identity.",
  },
  {
    key: "verify-cea",
    title: "Verify your CEA and brand credentials",
    body: "Submit your verification details so the platform can approve the agent identity that will appear on your Signature portal.",
  },
  {
    key: "activate-dashboard",
    title: "Complete payment and enter your live dashboard",
    body: "Once payment is successful, you move into the live Signature environment with your dashboard, lead flows, and advisory tools ready to use.",
  },
];

const FOR_AGENTS_HOW_TO_SCHEMA = {
  "@context": "https://schema.org",
  "@type": "HowTo",
  name: "How to launch your SG Prop Portal founding agent portal",
  description: "A four-step path for Singapore property agents to activate a live SG Prop Portal Signature dashboard.",
  totalTime: "P1D",
  step: AGENT_ACTIVATION_STEPS.map((step, index) => ({
    "@type": "HowToStep",
    position: index + 1,
    name: step.title,
    text: step.body,
  })),
};

const FoundingBanner = ({ testId = "for-agents-founding-banner" }) => (
  <div className="rounded-full border border-[#D4AF37]/30 bg-[linear-gradient(90deg,rgba(212,175,55,0.18),rgba(212,175,55,0.05),rgba(212,175,55,0.18))] px-5 py-3 text-center text-xs font-bold uppercase tracking-[0.18em] text-[#D4AF37]" data-testid={testId}>
    {OFFER_COPY}
  </div>
);

export const ForAgentsPage = () => {
  const openLivePortal = () => {
    if (typeof window === "undefined") return;
    const nextUrl = new URL("/agent/clive", window.location.origin).toString();
    window.location.assign(nextUrl);
  };
  const { buildBrandedPath, getWhatsAppHref, isAgentPath, leadFormPath } = useSiteBranding();
  const isRootDomainAllowed = assertRootDomainOnly(isAgentPath);
  const location = useLocation();
  const isRootSalesPage = location.pathname === "/";
  const [inventorySummary, setInventorySummary] = useState({ cleanTotal: 139, multiAgentPropertyTotal: 128 });

  useSeo({
    title: "For Agents | SG Prop Portal",
    description: "AI-native Singapore real estate SaaS for agents who want an Alpha Intelligence portal, Signature Advisor, and white-label advisory stack.",
  });

  useStructuredData("for-agents-howto-schema", FOR_AGENTS_HOW_TO_SCHEMA);

  useEffect(() => {
    const loadInventorySummary = async () => {
      try {
        const response = await api.get("/listings/public", { params: { page: 1, page_size: 1 } });
        setInventorySummary({
          cleanTotal: Number(response.data?.clean_total || response.data?.total || 139),
          multiAgentPropertyTotal: Number(response.data?.multi_agent_property_total || 128),
        });
      } catch {
        setInventorySummary({ cleanTotal: 139, multiAgentPropertyTotal: 128 });
      }
    };

    loadInventorySummary();
  }, []);

  const handleInstitutionalSearch = useCallback(
    (query) => {
      const params = new URLSearchParams();
      params.set("unique_listings", "true");
      if (query?.trim()) params.set("q", query.trim());
      window.location.assign(buildBrandedPath(`/listings?${params.toString()}`));
    },
    [buildBrandedPath],
  );

  const startPublicFoundingCheckout = useCallback(async () => {
    try {
      const response = await api.post("/billing/public-founding-seat-checkout", { origin_url: window.location.origin });
      if (response.data?.url) {
        window.location.assign(response.data.url);
      }
    } catch {
      window.open(getWhatsAppHref("Hello SG Prop Portal, I want to secure a Founding Seat."), "_blank", "noopener,noreferrer");
    }
  }, [getWhatsAppHref]);

  const openPricingPage = useCallback(() => {
    window.open(buildBrandedPath("/pricing"), "_blank", "noopener,noreferrer");
  }, [buildBrandedPath]);

  if (!isRootDomainAllowed) {
    return <Navigate replace to="/" />;
  }

  return (
    <div className={`min-h-screen bg-[#F8F7F4] text-[#0C285A] ${isRootSalesPage ? "root-sales-page" : ""}`.trim()} data-testid="for-agents-page">
      <section className="section-shell pt-8">
        <FoundingBanner testId="for-agents-founding-banner-top" />
      </section>

      <InstitutionalConversionHero buildBrandedPath={buildBrandedPath} onSearchSubmit={handleInstitutionalSearch} onSeatCheckoutStart={startPublicFoundingCheckout} uniqueCount={inventorySummary.cleanTotal} />

      <MarketActivityBanner uniqueCount={inventorySummary.cleanTotal} />

      <section className="section-shell pb-8 pt-10">
        <div className="grid gap-8 lg:grid-cols-[1.1fr_0.9fr] lg:items-start">
          <div className="flex h-full flex-col gap-7">
            <p className="text-xs font-bold uppercase tracking-[0.26em] text-[#8b6d16]" data-testid="for-agents-hero-kicker">Signature SaaS for Singapore Agents</p>
            <h1 className="font-[Cormorant_Garamond,serif] text-[2.1rem] font-bold leading-[0.98] text-[#0C285A] sm:text-[2.8rem] lg:text-[3.15rem]" data-testid="for-agents-hero-heading">Win more listings, respond faster, and build your own brand with Pro Plan.</h1>
            <p className="max-w-2xl text-sm leading-8 text-[#42566f] md:text-base" data-testid="for-agents-hero-subheading">A premium portal for agents who want stronger branding, faster follow-up, clearer buyer trust, and a more reliable path to owned lead flow.</p>
            <div className="grid gap-4 md:grid-cols-3" data-testid="for-agents-hero-commercial-proof-grid">
              {[
                { key: "brand", label: "Own your brand", value: "Branded portal" },
                { key: "response", label: "Respond faster", value: "24/7 lead flow" },
                { key: "roi", label: "Payback logic", value: "1 extra deal can justify months of Pro Plan" },
              ].map((item) => (
                <div className="rounded-[22px] border border-[#001935]/10 bg-white/84 px-4 py-4 shadow-[0_14px_36px_rgba(0,25,53,0.05)]" data-testid={`for-agents-hero-commercial-proof-${item.key}`} key={item.key}>
                  <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">{item.label}</p>
                  <p className="mt-3 text-base font-semibold leading-7 text-[#001935]">{item.value}</p>
                </div>
              ))}
            </div>
            <div className="max-w-2xl lg:flex-1" data-testid="for-agents-intent-split">
              <div className="grid gap-4 lg:h-full lg:min-h-[860px] lg:grid-rows-[0.4fr_1.6fr]">
                <div className="flex h-full flex-col justify-between rounded-[28px] border border-[#0C285A]/16 bg-[linear-gradient(180deg,#1A3F77_0%,#153767_100%)] p-5 backdrop-blur-xl shadow-[0_18px_50px_rgba(12,40,90,0.18)] transition duration-300 hover:-translate-y-0.5 hover:border-[#B8860B]/36 hover:shadow-[inset_0_0_0_1px_rgba(184,134,11,0.18),0_22px_58px_rgba(12,40,90,0.24)] lg:p-5">
                  <div>
                    <p className="text-xs font-bold uppercase tracking-[0.18em] text-[#B8860B]">Agent Path</p>
                    <h2 className="mt-3 font-[Cormorant_Garamond,serif] text-4xl font-bold text-white" data-testid="for-agents-intent-agent-heading">Launch Pro Plan like a business decision, not a software gamble.</h2>
                    <p className="mt-3 max-w-lg text-sm leading-7 text-[#F3E8D3]">Founding access gives you the fastest route into a branded portal, cleaner follow-up, and a more revenue-minded advisory system.</p>
                  </div>
                  <button className="gold-button mt-6 inline-flex w-fit" data-testid="for-agents-intent-agent-cta" onClick={startPublicFoundingCheckout} type="button">
                    Launch Pro Plan <Sparkles className="ml-2 h-4 w-4" />
                  </button>
                </div>

                <LeadSovereigntyCard className="h-full" onSeatCheckoutStart={startPublicFoundingCheckout} />
              </div>
            </div>
          </div>

          <InstitutionalRoiCalculator onSeatCheckoutStart={startPublicFoundingCheckout} />
        </div>
      </section>

      <section className="section-shell py-6" data-testid="for-agents-big-three-section">
        <div className="rounded-[36px] border border-[#001935]/10 bg-white/86 p-8 shadow-[0_22px_60px_rgba(0,25,53,0.06)] lg:p-10">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#8b6d16]" data-testid="for-agents-big-three-kicker">The Big Three Value Props</p>
            <h2 className="mt-3 text-4xl font-semibold leading-tight text-[#001935] md:text-5xl" data-testid="for-agents-big-three-heading">
              The clearest reasons serious agents upgrade to Pro Plan.
            </h2>
          </div>

          <div className="mt-8 grid gap-4 lg:grid-cols-3" data-testid="for-agents-big-three-grid">
            {BIG_THREE_VALUE_PROPS.map((item) => {
              const Icon = item.icon;
              return (
                <div className="rounded-[28px] border border-[#001935]/10 bg-[#fffaf1] px-6 py-6 shadow-[0_18px_45px_rgba(0,25,53,0.05)]" data-testid={`for-agents-big-three-card-${item.key}`} key={item.key}>
                  <div className="flex h-14 w-14 items-center justify-center rounded-full border border-[#b3935d]/28 bg-[#fff4db] text-[#b3935d]" data-testid={`for-agents-big-three-icon-${item.key}`}>
                    <Icon className="h-6 w-6" />
                  </div>
                  <h3 className="mt-6 text-2xl font-semibold leading-tight text-[#001935]" data-testid={`for-agents-big-three-title-${item.key}`}>
                    {item.title}
                  </h3>
                  <p className="mt-4 text-sm leading-8 text-[#42566f]" data-testid={`for-agents-big-three-body-${item.key}`}>
                    {item.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      <section className="section-shell py-6" data-testid="for-agents-automate-day-section">
        <div className="space-y-6 rounded-[36px] border border-[#D4AF37]/22 bg-[linear-gradient(135deg,#fffaf1_0%,#f7ecd6_48%,#eadbb4_100%)] p-8 shadow-[0_32px_90px_rgba(0,25,53,0.12)] lg:p-10">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#8b6d16]" data-testid="for-agents-automate-day-kicker">Automate Your Day</p>
            <h2 className="mt-3 text-4xl font-semibold leading-tight text-[#001935] md:text-5xl" data-testid="for-agents-automate-day-heading">
              Let the platform handle the repetitive work while you focus on closing.
            </h2>
          </div>

          <div className="grid gap-6 lg:grid-cols-[1fr_0.95fr] lg:items-center" data-testid="for-agents-automate-day-block-a">
            <div className="space-y-6">
              <div className="rounded-[24px] border border-[#001935]/10 bg-white/82 px-5 py-5 shadow-[0_16px_40px_rgba(0,25,53,0.05)]" data-testid="for-agents-automate-day-block-a-card-1">
                <h3 className="text-2xl font-semibold leading-tight text-[#001935]">Never Miss a Hot Prospect</h3>
                <p className="mt-3 text-sm leading-8 text-[#42566f]">Automated follow-ups mean every single inquiry gets an immediate response. You capture buyers at their highest point of interest.</p>
              </div>
              <div className="rounded-[24px] border border-[#001935]/10 bg-white/82 px-5 py-5 shadow-[0_16px_40px_rgba(0,25,53,0.05)]" data-testid="for-agents-automate-day-block-a-card-2">
                <h3 className="text-2xl font-semibold leading-tight text-[#001935]">Save Hours of Admin Work</h3>
                <p className="mt-3 text-sm leading-8 text-[#42566f]">The AI does the heavy lifting by pre-qualifying leads and gathering their requirements before you even pick up the phone.</p>
              </div>
            </div>

            <div className="rounded-[28px] border border-[#001935]/10 bg-[#001935] p-4 shadow-[0_22px_60px_rgba(0,25,53,0.14)]" data-testid="for-agents-dashboard-placeholder-shell">
              <div className="rounded-[24px] border border-white/10 bg-[linear-gradient(180deg,rgba(0,25,53,0.98),rgba(4,33,67,0.94))] p-5" data-testid="for-agents-dashboard-placeholder-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#b3935d]">Dashboard Placeholder</p>
                    <p className="mt-2 text-xl font-semibold text-white">Lead response command flow</p>
                  </div>
                  <div className="rounded-full border border-[#b3935d]/24 bg-[#b3935d]/12 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-[#F3E8D3]">Instant reply</div>
                </div>
                <div className="mt-6 grid gap-3 md:grid-cols-2">
                  <div className="rounded-[18px] border border-white/10 bg-white/6 p-4">
                    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#b3935d]">Incoming lead</p>
                    <p className="mt-3 text-base font-semibold text-white">Buyer asks at 11:43 PM</p>
                  </div>
                  <div className="rounded-[18px] border border-white/10 bg-white/6 p-4">
                    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#b3935d]">System action</p>
                    <p className="mt-3 text-base font-semibold text-white">AI qualifies and replies instantly</p>
                  </div>
                </div>
              </div>
            </div>
          </div>

          <div className="grid gap-6 lg:grid-cols-[0.95fr_1fr] lg:items-center" data-testid="for-agents-automate-day-block-b">
            <div className="order-2 rounded-[28px] border border-[#001935]/10 bg-[#001935] p-4 shadow-[0_22px_60px_rgba(0,25,53,0.14)] lg:order-1" data-testid="for-agents-report-placeholder-shell">
              <div className="rounded-[24px] border border-white/10 bg-[linear-gradient(180deg,rgba(0,25,53,0.98),rgba(4,33,67,0.94))] p-5" data-testid="for-agents-report-placeholder-card">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#b3935d]">Report Placeholder</p>
                    <p className="mt-2 text-xl font-semibold text-white">AI insight and analytics preview</p>
                  </div>
                  <div className="rounded-full border border-[#b3935d]/24 bg-[#b3935d]/12 px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-[#F3E8D3]">Live analytics</div>
                </div>
                <div className="mt-6 grid gap-3 md:grid-cols-2">
                  <div className="rounded-[18px] border border-white/10 bg-white/6 p-4">
                    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#b3935d]">Top clicked property</p>
                    <p className="mt-3 text-base font-semibold text-white">District 15 freehold family unit</p>
                  </div>
                  <div className="rounded-[18px] border border-white/10 bg-white/6 p-4">
                    <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#b3935d]">Buyer signal</p>
                    <p className="mt-3 text-base font-semibold text-white">High repeat view time and strong intent</p>
                  </div>
                </div>
              </div>
            </div>

            <div className="order-1 space-y-6 lg:order-2">
              <div className="rounded-[24px] border border-[#001935]/10 bg-white/82 px-5 py-5 shadow-[0_16px_40px_rgba(0,25,53,0.05)]" data-testid="for-agents-automate-day-block-b-card-1">
                <h3 className="text-2xl font-semibold leading-tight text-[#001935]">Smarter Client Meetings</h3>
                <p className="mt-3 text-sm leading-8 text-[#42566f]">Instantly generate AI property reports and market insights. You will walk into every viewing looking like the ultimate local expert.</p>
              </div>
              <div className="rounded-[24px] border border-[#001935]/10 bg-white/82 px-5 py-5 shadow-[0_16px_40px_rgba(0,25,53,0.05)]" data-testid="for-agents-automate-day-block-b-card-2">
                <h3 className="text-2xl font-semibold leading-tight text-[#001935]">Know Exactly What Buyers Want</h3>
                <p className="mt-3 text-sm leading-8 text-[#42566f]">Real-time analytics show you exactly which properties your visitors are clicking on and how long they look at them, taking the guesswork out of your follow-up.</p>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell py-6" data-testid="for-agents-long-term-value-section">
        <div className="rounded-[36px] border border-[#001935]/10 bg-white/86 p-8 shadow-[0_22px_60px_rgba(0,25,53,0.06)] lg:p-10">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#8b6d16]" data-testid="for-agents-long-term-value-kicker">Long-Term Value</p>
            <h2 className="mt-3 text-4xl font-semibold leading-tight text-[#001935] md:text-5xl" data-testid="for-agents-long-term-value-heading">
              The upside is bigger than short-term lead handling.
            </h2>
          </div>

          <div className="mt-8 grid gap-4 lg:grid-cols-2" data-testid="for-agents-long-term-value-grid">
            {LONG_TERM_VALUE_POINTS.map((item) => (
              <div className="rounded-[28px] border border-[#001935]/10 bg-[#fffaf1] px-6 py-6 shadow-[0_18px_45px_rgba(0,25,53,0.05)]" data-testid={`for-agents-long-term-value-card-${item.key}`} key={item.key}>
                <h3 className="text-2xl font-semibold leading-tight text-[#001935]">{item.title}</h3>
                <p className="mt-4 text-sm leading-8 text-[#42566f]">{item.body}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-shell py-6" data-testid="for-agents-roi-justification-section">
        <div className="rounded-[36px] border border-[#D4AF37]/22 bg-[linear-gradient(135deg,#fffaf1_0%,#f7ecd6_48%,#eadbb4_100%)] p-8 shadow-[0_32px_90px_rgba(0,25,53,0.12)] lg:p-10">
          <div className="grid gap-8 lg:grid-cols-[0.88fr_1.12fr] lg:items-start">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#8b6d16]" data-testid="for-agents-roi-justification-kicker">Why it’s worth it</p>
              <h2 className="mt-3 text-4xl font-semibold leading-tight text-[#001935] md:text-5xl" data-testid="for-agents-roi-justification-heading">
                One extra deal can justify months of Pro Plan.
              </h2>
              <p className="mt-4 text-sm leading-8 text-[#42566f] md:text-base" data-testid="for-agents-roi-justification-copy">
                The Pro Plan should be judged like a revenue decision, not just a software subscription. Stronger branding, faster response, and better buyer trust can create outsized upside from a relatively small monthly cost.
              </p>
              <div className="mt-6 flex flex-wrap gap-3">
                <button className="gold-button" data-testid="for-agents-roi-justification-primary-cta" onClick={startPublicFoundingCheckout} type="button">
                  Launch Pro Plan <ArrowRight className="ml-2 h-4 w-4" />
                </button>
                <button className="outline-button border-[#001935]/14 text-[#001935] hover:bg-[#001935]/5" data-testid="for-agents-roi-justification-demo-cta" onClick={openLivePortal} type="button">
                  View Live Demo
                </button>
              </div>
            </div>

            <div className="grid gap-4 md:grid-cols-2" data-testid="for-agents-roi-justification-grid">
              {ROI_PROOF_POINTS.map((point, index) => (
                <div className="rounded-[24px] border border-[#001935]/10 bg-white/82 px-5 py-5 shadow-[0_16px_40px_rgba(0,25,53,0.05)]" data-testid={`for-agents-roi-justification-card-${index + 1}`} key={point}>
                  <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">ROI point {String(index + 1).padStart(2, "0")}</p>
                  <p className="mt-3 text-base font-semibold leading-7 text-[#001935]">{point}</p>
                </div>
              ))}
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell py-6" data-testid="for-agents-setup-easy-section">
        <div className="rounded-[34px] border border-[#D4AF37]/22 bg-[linear-gradient(135deg,#fffaf1_0%,#f7ecd6_48%,#eadbb4_100%)] p-8 shadow-[0_28px_80px_rgba(0,25,53,0.1)] lg:p-10">
          <div className="flex flex-col gap-5 lg:flex-row lg:items-center lg:justify-between">
            <div className="flex items-start gap-4">
              <div className="flex h-14 w-14 shrink-0 items-center justify-center rounded-full border border-[#b3935d]/28 bg-[#fff4db] text-[#b3935d]" data-testid="for-agents-setup-easy-icon">
                <Zap className="h-6 w-6" />
              </div>
              <div>
                <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#8b6d16]" data-testid="for-agents-setup-easy-kicker">Setup is Easy</p>
                <h2 className="mt-3 text-4xl font-semibold leading-tight text-[#001935] md:text-5xl" data-testid="for-agents-setup-easy-heading">
                  Live in Minutes, No Tech Skills Needed
                </h2>
                <p className="mt-4 max-w-3xl text-sm leading-8 text-[#42566f] md:text-base" data-testid="for-agents-setup-easy-copy">
                  Just verify your CEA number, upload your photo into the setup wizard, and your custom portal is instantly ready to go.
                </p>
              </div>
            </div>

            <button className="gold-button w-full justify-center lg:w-auto" data-testid="for-agents-setup-easy-cta" onClick={startPublicFoundingCheckout} type="button">
              Start Setup <ArrowRight className="ml-2 h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      <section className="section-shell py-6">
        <SubscriptionPlansTable />
      </section>

      <section className="section-shell py-6">
        <RealTimeAssetIntelligenceStrip />
      </section>

      <section className="section-shell py-6">
        <div className="grid gap-6 rounded-[36px] border border-[#D4AF37]/22 bg-[linear-gradient(135deg,#fffaf1_0%,#f7ecd6_48%,#eadbb4_100%)] p-8 shadow-[0_32px_90px_rgba(0,25,53,0.12)] lg:grid-cols-[0.82fr_1.18fr] lg:p-10" data-testid="for-agents-flagship-section">
          <div className="space-y-5">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#8b6d16]">Live Demo / Real Portal Proof</p>
            <h2 className="text-4xl font-semibold leading-tight text-[#001935] md:text-5xl" data-testid="for-agents-flagship-heading">See a live agent portal in action.</h2>
            <p className="text-sm leading-8 text-[#42566f] md:text-base">Preview what buyers see, how your brand is presented, and how the Pro Plan turns a profile into a more complete advisory website.</p>
            <div className="flex flex-wrap gap-3">
              <button className="gold-button" data-testid="for-agents-flagship-demo-button" onClick={openLivePortal} type="button">
                View Live Demo <ArrowRight className="ml-2 h-4 w-4" />
              </button>
              <Link className="outline-button border-[#001935]/14 text-[#001935] hover:bg-[#001935]/5" data-testid="for-agents-flagship-onboarding-button" to="/signup?plan=pro">
                Launch Pro Plan
              </Link>
            </div>
          </div>

          <div className="rounded-[28px] border border-[#001935]/10 bg-white/80 p-4" data-testid="for-agents-flagship-mockup-shell">
            <div className="rounded-[26px] border border-[#001935]/10 bg-[#fffaf1]/95 p-3 shadow-[0_18px_60px_rgba(0,25,53,0.08)]">
              <div className="mb-3 flex items-center gap-2 px-2 text-[#8b6d16]/58">
                <span className="h-2.5 w-2.5 rounded-full bg-[#D4AF37]" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#D4AF37]/60" />
                <span className="h-2.5 w-2.5 rounded-full bg-[#D4AF37]/35" />
              </div>
              <div className="overflow-hidden rounded-[22px] border border-[#001935]/10 bg-white" data-testid="for-agents-flagship-mockup-frame">
                <div className="flex h-[440px] flex-col justify-between bg-[linear-gradient(180deg,#ffffff_0%,#f8f1e3_100%)] p-6" data-testid="for-agents-flagship-mockup-preview">
                  <div>
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Flagship Preview</p>
                        <h3 className="mt-2 text-2xl font-semibold text-[#001935]">Clive Signature Portal</h3>
                      </div>
                      <span className="rounded-full border border-[#D4AF37]/28 bg-[#fff6df] px-3 py-1 text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">Live Demo</span>
                    </div>
                    <p className="mt-4 max-w-lg text-sm leading-7 text-[#42566f]">Preview the premium editorial shell, shortlist studio, and Signature Advisor journey without loading the live agent route inside the marketing page.</p>
                  </div>
                  <div className="grid gap-3 md:grid-cols-3">
                    {[
                      ["Listings", "263"],
                      ["Advisor Flow", "24/7"],
                      ["District Intel", "Live"],
                    ].map(([label, value]) => (
                      <div className="rounded-[18px] border border-[#001935]/10 bg-white/80 p-4" key={label}>
                        <p className="text-[11px] font-bold uppercase tracking-[0.16em] text-[#8b6d16]">{label}</p>
                        <p className="mt-2 text-xl font-semibold text-[#001935]">{value}</p>
                      </div>
                    ))}
                  </div>
                  <div className="mt-5 flex flex-wrap gap-3">
                    <button className="gold-button" data-testid="for-agents-flagship-mockup-open-button" onClick={openLivePortal} type="button">
                      Open Live Portal <ArrowRight className="ml-2 h-4 w-4" />
                    </button>
                    <p className="text-xs leading-6 text-[#42566f]">Interactive preview opens only when you choose to view it, keeping the main site crawl path clean.</p>
                  </div>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <section className="section-shell py-6">
        <div className="grid gap-5 lg:grid-cols-3">
          {FEATURE_CARDS.map((card) => {
            const Icon = card.icon;
            return (
              <div className="rounded-[32px] border border-[#001935]/10 bg-white/80 p-6 shadow-[0_18px_50px_rgba(0,25,53,0.08)]" data-testid={`for-agents-feature-card-${card.key}`} key={card.key}>
                <div className="flex h-12 w-12 items-center justify-center rounded-full border border-[#D4AF37]/25 bg-[#fff4db] text-[#8b6d16]">
                  <Icon className="h-5 w-5" />
                </div>
                <h3 className="mt-6 text-4xl font-semibold leading-tight text-[#001935]">{card.title}</h3>
                <p className="mt-4 text-sm leading-8 text-[#42566f]">{card.body}</p>
              </div>
            );
          })}
        </div>
      </section>

      <section className="section-shell py-6" data-testid="for-agents-final-conversion-cta-section">
        <div className="rounded-[34px] border border-[#D4AF37]/22 bg-[linear-gradient(135deg,#fffaf1_0%,#f7ecd6_48%,#eadbb4_100%)] p-8 text-center shadow-[0_28px_80px_rgba(0,25,53,0.1)]">
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#8b6d16]">Final Conversion CTA</p>
          <h2 className="mt-4 text-4xl font-semibold leading-tight text-[#001935] md:text-5xl">Launch your Pro Plan and start owning your pipeline.</h2>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-8 text-[#42566f] md:text-base">
            If you want stronger branding, faster follow-up, and better lead ownership, the next step is simple: view the live proof or start your Pro Plan activation.
          </p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <button className="gold-button" data-testid="for-agents-final-conversion-primary-cta" onClick={startPublicFoundingCheckout} type="button">
              Launch Pro Plan <ArrowRight className="ml-2 h-4 w-4" />
            </button>
            <button className="outline-button border-[#001935]/14 text-[#001935] hover:bg-[#001935]/5" data-testid="for-agents-final-conversion-demo-cta" onClick={openLivePortal} type="button">
              View Live Demo
            </button>
            <a className="outline-button border-[#001935]/14 text-[#001935] hover:bg-[#001935]/5" data-testid="for-agents-final-conversion-contact-cta" href={getWhatsAppHref("Hello SG Prop Portal, I want to understand the Pro Plan.")} rel="noreferrer" target="_blank">
              Contact Sales
            </a>
          </div>
        </div>
      </section>

      <FoundingRevenueBlock onSeatCheckoutStart={startPublicFoundingCheckout} />

      <section className="section-shell py-6" data-testid="for-agents-activation-howto-section">
        <div className="rounded-[36px] border border-[#D4AF37]/22 bg-[linear-gradient(135deg,#fffaf1_0%,#f7ecd6_48%,#eadbb4_100%)] p-8 shadow-[0_32px_90px_rgba(0,25,53,0.12)] lg:p-10">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#8b6d16]" data-testid="for-agents-activation-howto-kicker">Activation Path</p>
            <h2 className="mt-3 text-4xl font-semibold leading-tight text-[#001935] md:text-5xl" data-testid="for-agents-activation-howto-heading">
              How a founding agent goes live.
            </h2>
            <p className="mt-4 text-sm leading-8 text-[#42566f] md:text-base" data-testid="for-agents-activation-howto-copy">
              This mirrors the onboarding path already described across the sales page: account creation, verification, payment, and immediate access to a live Signature workspace.
            </p>
          </div>

          <div className="mt-8 grid gap-4 md:grid-cols-2" data-testid="for-agents-activation-howto-grid">
            {AGENT_ACTIVATION_STEPS.map((step, index) => (
              <div className="rounded-[24px] border border-[#001935]/10 bg-white/82 px-5 py-5 shadow-[0_16px_40px_rgba(0,25,53,0.05)]" data-testid={`for-agents-activation-step-${index + 1}`} key={step.key}>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]" data-testid={`for-agents-activation-step-label-${index + 1}`}>
                  Step {String(index + 1).padStart(2, "0")}
                </p>
                <h3 className="mt-3 text-xl font-semibold leading-tight text-[#001935]" data-testid={`for-agents-activation-step-title-${index + 1}`}>
                  {step.title}
                </h3>
                <p className="mt-3 text-sm leading-8 text-[#42566f] md:text-base" data-testid={`for-agents-activation-step-body-${index + 1}`}>
                  {step.body}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="section-shell py-6" data-testid="for-agents-objection-faq-section">
        <div className="rounded-[36px] border border-[#001935]/10 bg-white/86 p-8 shadow-[0_22px_60px_rgba(0,25,53,0.06)] lg:p-10">
          <div className="max-w-3xl">
            <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#8b6d16]" data-testid="for-agents-objection-faq-kicker">Agent objections</p>
            <h2 className="mt-3 text-4xl font-semibold leading-tight text-[#001935] md:text-5xl" data-testid="for-agents-objection-faq-heading">
              Frequently Asked Questions (FAQs)
            </h2>
          </div>

          <div className="mt-8 grid gap-4 lg:grid-cols-2" data-testid="for-agents-objection-faq-grid">
            {AGENT_OBJECTION_FAQ.map((item, index) => (
              <div className="rounded-[24px] border border-[#001935]/10 bg-[#fffaf1] px-5 py-5" data-testid={`for-agents-objection-faq-card-${item.key}`} key={item.key}>
                <p className="text-[11px] font-bold uppercase tracking-[0.18em] text-[#8b6d16]">Question {String(index + 1).padStart(2, "0")}</p>
                <h3 className="mt-3 text-xl font-semibold leading-tight text-[#001935]">{item.question}</h3>
                <p className="mt-3 text-sm leading-7 text-[#42566f]">{item.answer}</p>
              </div>
            ))}
          </div>
        </div>
      </section>


      <section className="section-shell pb-14 pt-6">
        <div className="rounded-[34px] border border-[#D4AF37]/22 bg-[linear-gradient(135deg,#fffaf1_0%,#f7ecd6_48%,#eadbb4_100%)] p-8 text-center shadow-[0_28px_80px_rgba(0,25,53,0.1)]" data-testid="for-agents-final-cta-section">
          <p className="text-xs font-bold uppercase tracking-[0.24em] text-[#8b6d16]">Founding Agent Access</p>
          <h2 className="mt-4 text-4xl font-semibold leading-tight text-[#001935] md:text-5xl">Turn your next serious client conversation into the reason you launched Pro Plan.</h2>
          <p className="mx-auto mt-4 max-w-2xl text-sm leading-8 text-[#42566f] md:text-base">Create your account, verify your CEA, complete payment, and move into a branded system designed to help you look stronger, respond faster, and convert with more confidence.</p>
          <div className="mt-6 flex flex-wrap justify-center gap-3">
            <Link className="gold-button" data-testid="for-agents-final-primary-cta" to="/signup?plan=pro">
              Launch My Pro Plan <Sparkles className="ml-2 h-4 w-4" />
            </Link>
            <button className="outline-button border-[#001935]/14 text-[#001935] hover:bg-[#001935]/5" data-testid="for-agents-final-demo-link" onClick={openLivePortal} type="button">
              View Live Demo
            </button>
          </div>
        </div>
      </section>

      <section className="section-shell pt-2" data-testid="founding-status-banner-shell">
        <div className="rounded-full border border-[#D4AF37]/26 bg-[linear-gradient(90deg,rgba(212,175,55,0.2),rgba(212,175,55,0.06),rgba(212,175,55,0.2))] px-6 py-4 text-center text-sm font-semibold text-[#D4AF37] shadow-[0_18px_50px_rgba(0,0,0,0.12)]" data-testid="founding-status-banner">
          <span>Our Founding 11 network is currently active. <strong>[Only 4 seats remaining].</strong></span>
          <button className="gold-button ml-4 inline-flex align-middle" data-testid="founding-status-banner-cta" onClick={startPublicFoundingCheckout} type="button">
            Lock in Founding Benefits <Sparkles className="ml-2 h-4 w-4" />
          </button>
        </div>
      </section>

      {isRootSalesPage ? <FloatingOwnershipBot onCtaClick={openPricingPage} /> : null}
    </div>
  );
};